import React, { useState } from 'react'
import { useStorePerformance, useFiscalWeeks, useKPIs, useStoreTopSKUs } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import { Channel } from '../../../shared/schema'
import { parseFiscalWeek } from '../../../shared/analytics'
import { 
  Store, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle,
  Calendar,
  Filter,
  Search,
  RefreshCw,
  Package,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react'

export default function Stores() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [selectedChannel, setSelectedChannel] = useState<Channel>('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [showTopSKUs, setShowTopSKUs] = useState(false)
  const [sortField, setSortField] = useState<'revenue' | 'units' | 'yoyGrowth' | 'store_name'>('revenue')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Data fetching
  const { data: stores, isLoading, error, refetch } = useStorePerformance(selectedFiscalWeek, selectedChannel)
  const { data: fiscalWeeks } = useFiscalWeeks()
  const { data: kpis } = useKPIs(selectedFiscalWeek)
  const { data: topStoreSkus } = useStoreTopSKUs(selectedFiscalWeek, 'revenue', 10)


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High':
        return <AlertTriangle className="w-4 h-4 text-red-500" />
      case 'Medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />
      default:
        return <CheckCircle className="w-4 h-4 text-green-500" />
    }
  }

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High':
        return 'text-red-500 bg-red-500/10'
      case 'Medium':
        return 'text-yellow-500 bg-yellow-500/10'
      default:
        return 'text-green-500 bg-green-500/10'
    }
  }

  const handleSort = (field: 'revenue' | 'units' | 'yoyGrowth' | 'store_name') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const filteredAndSortedStores = stores?.filter(store =>
    store.store_name.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    let aValue: any = a[sortField]
    let bValue: any = b[sortField]
    
    if (sortField === 'store_name') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  }) || []

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load store data</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Global Header */}
      <GlobalHeader 
        title="Store Performance"
        subtitle="Individual store metrics and risk assessment"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Search Filter */}
        <div className="flex items-center space-x-2 max-w-md">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search stores..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {/* Main KPIs */}
      {filteredAndSortedStores.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Stores</p>
            <p className="text-2xl font-bold text-foreground">{filteredAndSortedStores.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Store Revenue</p>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency((kpis?.totalRevenue || 0) - (kpis?.dotcomSales || 0))}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              From Summary Data (Source of Truth)
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">High Risk Stores</p>
            <p className="text-2xl font-bold text-red-500">
              {filteredAndSortedStores.filter(store => store.riskLevel === 'High').length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Avg YoY Growth</p>
            <p className="text-2xl font-bold text-foreground">
              {filteredAndSortedStores.length > 0 ? formatPercentage(
                filteredAndSortedStores.reduce((sum, store) => sum + store.yoyGrowth, 0) / filteredAndSortedStores.length
              ) : '0%'}
            </p>
          </div>
        </div>
      )}

      {/* Store Channel Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Comp Sales */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Comp Sales</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Revenue</span>
              <span className="font-semibold text-foreground">
                {formatCurrency(kpis?.compSales || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">% of Total</span>
              <span className="font-semibold text-foreground">
                {kpis ? formatPercentage((kpis.compSales / kpis.totalRevenue) * 100) : '0%'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">YoY Growth</span>
              <div className="flex items-center space-x-1">
                {(kpis?.compSalesYoyGrowth || 0) >= 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-500" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-500" />
                )}
                <span className={(kpis?.compSalesYoyGrowth || 0) >= 0 ? 'text-green-500' : 'text-red-500'}>
                  {formatPercentage((kpis?.compSalesYoyGrowth || 0) * 100)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Store Type</span>
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                Open 13+ months
              </span>
            </div>
          </div>
        </div>

        {/* Non-Comp Sales */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Non-Comp Sales</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Revenue</span>
              <span className="font-semibold text-foreground">
                {formatCurrency(kpis?.nonCompSales || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">% of Total</span>
              <span className="font-semibold text-foreground">
                {kpis ? formatPercentage((kpis.nonCompSales / kpis.totalRevenue) * 100) : '0%'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">YoY Growth</span>
              <div className="flex items-center space-x-1">
                {(kpis?.nonCompSalesYoyGrowth || 0) >= 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-500" />
                ) : (
                  <TrendingDown className="w-3 h-3 text-red-500" />
                )}
                <span className={(kpis?.nonCompSalesYoyGrowth || 0) >= 0 ? 'text-green-500' : 'text-red-500'}>
                  {formatPercentage((kpis?.nonCompSalesYoyGrowth || 0) * 100)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Store Type</span>
              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                Open &lt;13 months
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Store Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('store_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Store</span>
                    {sortField === 'store_name' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('revenue')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Revenue</span>
                    {sortField === 'revenue' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('units')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Units</span>
                    {sortField === 'units' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('yoyGrowth')}
                >
                  <div className="flex items-center space-x-1">
                    <span>YoY Growth</span>
                    {sortField === 'yoyGrowth' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <span>Risk Level</span>
                    <div className="group relative">
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      <div className="invisible group-hover:visible absolute top-0 right-0 mt-6 w-64 p-3 bg-popover border border-border rounded-lg text-xs text-popover-foreground shadow-xl z-[9999]">
                        <strong>Risk Levels:</strong><br/>
                        • <span className="text-green-400">Healthy</span>: Revenue &gt; $200<br/>
                        • <span className="text-yellow-400">Medium</span>: Revenue $50-$200<br/>
                        • <span className="text-red-400">High</span>: Revenue &lt; $50<br/>
                        <br/>
                        <em>Note: Many stores have $0 revenue (normal - not all stores sell Buttah products each week)</em>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading store data...
                  </td>
                </tr>
              ) : filteredAndSortedStores.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No stores found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredAndSortedStores.map((store, index) => (
                  <tr key={`${store.store_name}-${index}`} className="border-t border-border hover:bg-muted/25">
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Store className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-foreground">{store.store_name}</span>
                      </div>
                    </td>
                    <td className="p-4 font-medium text-foreground">
                      {formatCurrency(store.revenue)}
                    </td>
                    <td className="p-4 text-foreground">
                      {formatNumber(store.units)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        {store.yoyGrowth >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        )}
                        <span className={store.yoyGrowth >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {formatPercentage(store.yoyGrowth)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(store.riskLevel)}`}>
                        {getRiskIcon(store.riskLevel)}
                        <span>{store.riskLevel}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  )
}
